import { useEffect, useMemo, useReducer } from "react";
import { Col, Row } from "src/components/layout";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  type CurrentAddressFormData,
  toCreateCurrentAddressReq,
  toUpdateCurrentAddressReq,
} from "src/api/adapters/address";
import { housing_type_data, month_data, year_data } from "src/constants";
import { googlePlacesFieldProps } from "src/constants/googlePlaces";
import {
  AddressFields,
  Checkbox,
  FormTable,
  FormTableBody,
  Input,
  Label,
  Location06Icon,
  Select,
  SelectItem,
  SectionTitle,
  shouldClearOtherField,
} from "@porchpass/ui";
import { DomainAddressType, DomainHousingType } from "@porchpass/api-client";
import {
  addressErrorsAfterSubmit,
  fromUiAddressPatch,
  toUiAddress,
} from "src/forms/adapters/address";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import {
  lessThanMaxLength,
  notEmpty,
  validName,
  validStreetAddress,
  validZipcode,
  validate,
  test,
  type ValidationMapperFunc,
  validDateRange,
  greaterThanMinAmount,
} from "src/utils";
import { testOtherTextRequired } from "src/forms/validation/conditionalOther";
import { DLA_ADDRESS_HOUSING_OTHER } from "src/forms/validation/conditionalOtherConfigs";

export const validationMapper: ValidationMapperFunc<CurrentAddressFormData> = (
  formData,
) => ({
  street1: [
    test(notEmpty, "Address must not be empty"),
    test(validStreetAddress, "Must be a valid street address"),
    test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
  ],
  street2: [
    test(validStreetAddress, "Must be a valid street address"),
    test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
  ],
  city: [
    test(notEmpty, "City must not be empty"),
    test(lessThanMaxLength(70), "City must not exceed 70 characters"),
  ],
  county: [
    test(validName, "Must be a valid county name"),
    test(lessThanMaxLength(70), "County must not exceed 70 characters"),
  ],
  state: [test(notEmpty, "State must not be empty")],
  zip_code: [
    test(notEmpty, "Zip Code must not be empty"),
    test(validZipcode, "Must be a valid US zip code"),
  ],
  from_month: [
    test(notEmpty, "Must select a month"),
    test(
      () =>
        validDateRange(
          formData.from_month ?? "",
          formData.from_year ?? "",
          (new Date().getMonth() + 1).toString().padStart(2, "0"),
          new Date().getFullYear().toString(),
        ),
      "Start date must be in the past",
    ),
  ],
  from_year: [test(notEmpty, "Must select a year")],
  housing_type: [test(notEmpty, "Housing type must be specified")],
  housing_type_other:
    formData.housing_type === DomainHousingType.HousingOwn ||
    formData.housing_type === DomainHousingType.HousingOther
      ? [
          testOtherTextRequired(
            formData.housing_type,
            DLA_ADDRESS_HOUSING_OTHER,
            undefined,
            "Must Explain Plan for Home",
          ),
        ]
      : [],
  monthly_payment:
    formData.housing_type !== "NO_PRIMARY_HOUSING_EXPENSE"
      ? [
          test(notEmpty, "Must enter a monthly payment amount"),
          test(
            greaterThanMinAmount(0, formData.housing_type === "OWN"),
            "Monthly payment amount must be greater than 0",
          ),
        ]
      : [],
});

type CreateAddressModalProps = {
  address?: CurrentAddressFormData & { id?: string };
  closeHandler: () => void;
  confirmHandler: () => void;
  hasMailingAddress: boolean;
};

export default ({
  address,
  closeHandler,
  confirmHandler,
  hasMailingAddress,
}: CreateAddressModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<CurrentAddressFormData>(
    { ...address },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );

  const [isMailingCheckboxSelected, flipIsMailingCheckboxSelected] = useReducer(
    (x) => !x,
    address?.id ? (formData.is_mailing_address ?? false) : !hasMailingAddress,
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: CurrentAddressFormData) =>
      address?.id
        ? api.updateAddress(address.id, toUpdateCurrentAddressReq(data))
        : api.createAddress(toCreateCurrentAddressReq(data)),
    );

  useEffect(() => {
    if (response) {
      confirmHandler();
    }
  }, [response]);

  useEffect(() => {
    updateField("is_mailing_address")(isMailingCheckboxSelected);
  }, [isMailingCheckboxSelected]);

  const showMailingAddress = address?.id ? true : !hasMailingAddress;

  const formErrors = validate<CurrentAddressFormData>(
    formData,
    validationMapper,
  );

  const addressValue = useMemo(
    () => toUiAddress(formData, DomainAddressType.AddressCurrent),
    [formData],
  );

  const addressErrors = useMemo(
    () => addressErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const housingErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          fromMonth: formErrors.from_month?.message,
          fromYear: formErrors.from_year?.message,
          housingType: formErrors.housing_type?.message,
          housingTypeOther: formErrors.housing_type_other?.message,
          monthlyPayment: formErrors.monthly_payment?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyAddressChange = (patch: Partial<typeof addressValue>) => {
    const mapped = fromUiAddressPatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof CurrentAddressFormData)(val);
      }
    }
  };

  return (
    <SlideUpModal
      title="What's your current address"
      isOpen={true}
      openSession={address}
      closeHandler={closeHandler}
      hasDismissButton={true}
      actionButtonText={address?.id ? "Save" : "Add Address"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Current Address"
        headerIcon={
          <Location06Icon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        }
      >
        <FormTableBody className="space-y-4">
          <AddressFields
            address={addressValue}
            errors={addressErrors}
            onFieldChange={(field, value) => {
              applyAddressChange({ [field]: value });
            }}
            onBulkChange={applyAddressChange}
            {...googlePlacesFieldProps}
          />

          <div className="space-y-3 border-t border-surface-container-disabled pt-4">
            <SectionTitle title="When did you begin living there?" size="md" />
            <Row>
              <Col>
                <Select
                  value={formData.from_month ?? ""}
                  placeholder="Month"
                  onValueChange={(value) => updateField("from_month")(value)}
                  error={!!housingErrors.fromMonth}
                  helperText={housingErrors.fromMonth}
                >
                  {month_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  value={formData.from_year ?? ""}
                  placeholder="Year"
                  onValueChange={(value) => updateField("from_year")(value)}
                  error={!!housingErrors.fromYear}
                  helperText={housingErrors.fromYear}
                >
                  {year_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
            </Row>

            <Select
              value={formData.housing_type ?? ""}
              placeholder="Housing Type"
              onValueChange={(value) => {
                if (value === "NO_PRIMARY_HOUSING_EXPENSE") {
                  updateField("monthly_payment")("");
                }
                if (shouldClearOtherField(value, DLA_ADDRESS_HOUSING_OTHER)) {
                  updateField("housing_type_other")("");
                }
                updateField("housing_type")(value);
              }}
              error={!!housingErrors.housingType}
              helperText={housingErrors.housingType}
            >
              {housing_type_data.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.label}
                </SelectItem>
              ))}
            </Select>

            {(formData.housing_type === "OWN" ||
              formData.housing_type === "OTHER") && (
              <Input
                label="Plans for home"
                value={formData.housing_type_other ?? ""}
                onChange={(e) =>
                  updateField("housing_type_other")(e.target.value)
                }
                error={!!housingErrors.housingTypeOther}
                helperText={housingErrors.housingTypeOther}
              />
            )}

            {formData.housing_type !== "NO_PRIMARY_HOUSING_EXPENSE" ? (
              <Input
                label="Monthly Payment"
                type="text"
                inputMode="decimal"
                leftSlot="$"
                value={formData.monthly_payment ?? ""}
                onChange={(e) => updateField("monthly_payment")(e.target.value)}
                error={!!housingErrors.monthlyPayment}
                helperText={
                  housingErrors.monthlyPayment ??
                  "Just the amount you personally pay"
                }
              />
            ) : (
              <input
                type="hidden"
                name="monthly_payment"
                value={0}
                onChange={updateField("housing_type_other")}
              />
            )}

            {showMailingAddress && (
              <div className="space-y-3 border-t border-surface-container-disabled pt-4">
                <SectionTitle title="What's your mailing address" size="md" />
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_mailing_address"
                    checked={isMailingCheckboxSelected}
                    onCheckedChange={flipIsMailingCheckboxSelected}
                  />
                  <Label
                    htmlFor="is_mailing_address"
                    className="font-normal leading-5"
                  >
                    Same as current address
                  </Label>
                </div>
              </div>
            )}
          </div>
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
